import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { getAdminSection } from '../../admin/adminSections';
import {
  createAdminCategory,
  deleteAdminCategory,
  listAdminCategories,
  updateAdminCategory,
} from '../../services/adminCategoryService';

const initialFormValues = {
  name: '',
  icon: '',
  image: '',
  sort_order: '',
};

function normalizeCategory(item) {
  return {
    category_id:
      item.category_id ??
      item.id ??
      item.categoryId ??
      '',
    name: item.name ?? '',
    icon: item.icon ?? '',
    image: item.image ?? '',
    sort_order: item.sort_order ?? item.sortOrder ?? '',
  };
}

export default function CategoriesPage() {
  const section = getAdminSection('categories');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formValues, setFormValues] = useState(initialFormValues);

  const visibleColumns = useMemo(() => {
    const fields = [
      { key: 'name', label: 'Name' },
      { key: 'icon', label: 'Icon' },
      { key: 'image', label: 'Image' },
      { key: 'sort_order', label: 'Sort Order' },
    ];

    if (isMobile) {
      return fields.slice(0, 2);
    }

    if (isTablet) {
      return fields.slice(0, 3);
    }

    return fields;
  }, [isMobile, isTablet]);

  const headerMetrics = [
    { label: 'Total categories', value: String(items.length) },
    { label: 'Sortable fields', value: '4' },
    { label: 'Ready for catalog', value: String(items.length) },
  ];

  const fetchCategories = async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }

    try {
      const response = await listAdminCategories();
      setItems(response.items.map(normalizeCategory));
      setSubmitError(null);
    } catch (error) {
      setSubmitError(error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleFieldChange = (field) => (event) => {
    setFormValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));

    setFieldErrors((current) => ({
      ...current,
      [field]: '',
    }));

    setSubmitError(null);
    setSubmitSuccess('');
  };

  const resetForm = () => {
    setFormValues(initialFormValues);
    setEditingId(null);
    setFieldErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.name.trim()) {
      errors.name = 'Category name is required.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSubmitError({
        message: 'Please review the category form before submitting.',
      });
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    setSubmitSuccess('');
    setFieldErrors({});

    try {
      if (editingId) {
        const result = await updateAdminCategory({
          category_id: editingId,
          ...formValues,
        });
        setSubmitSuccess(result?.message || 'Category updated successfully.');
      } else {
        const result = await createAdminCategory(formValues);
        setSubmitSuccess(result?.message || 'Category created successfully.');
      }

      resetForm();
      await fetchCategories(false);
    } catch (error) {
      setSubmitError(error);
      setFieldErrors(error.validationErrors || {});
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.category_id);
    setFormValues({
      name: item.name || '',
      icon: item.icon || '',
      image: item.image || '',
      sort_order: item.sort_order || '',
    });
    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleDelete = async (item) => {
    setDeletingId(item.category_id);
    setSubmitError(null);
    setSubmitSuccess('');

    try {
      const result = await deleteAdminCategory(item.category_id);
      if (editingId === item.category_id) {
        resetForm();
      }
      setSubmitSuccess(result?.message || 'Category deleted successfully.');
      await fetchCategories(false);
    } catch (error) {
      setSubmitError(error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Grid container spacing={2.5} className='admin-dashboard-metrics'>
        {headerMetrics.map((metric) => (
          <Grid key={metric.label} size={{ xs: 12, md: 4 }}>
            <Paper className='admin-dashboard-metricCard' elevation={0}>
              <Typography className='admin-dashboard-metricLabel'>
                {metric.label}
              </Typography>
              <Typography className='admin-dashboard-metricValue'>
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, xl: 5 }}>
          <Paper className='admin-dashboard-featureCard admin-crud-formCard' elevation={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  {editingId ? 'Edit category' : 'Create category'}
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {editingId ? 'Update category' : 'Add category'}
                </Typography>
              </Box>
              <Chip
                label={editingId ? 'Edit mode' : 'Live API'}
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-cardText'>
              Manage category records against the protected admin categories
              APIs using the stored admin bearer token.
            </Typography>

            {submitError ? (
              <Alert severity='error' className='auth-alert'>
                {submitError.message}
              </Alert>
            ) : null}

            {submitSuccess ? (
              <Alert severity='success' className='auth-alert'>
                {submitSuccess}
              </Alert>
            ) : null}

            <Box component='form' onSubmit={handleSubmit} className='admin-crud-form'>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Name'
                    value={formValues.name}
                    onChange={handleFieldChange('name')}
                    error={Boolean(fieldErrors.name)}
                    helperText={fieldErrors.name}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Icon'
                    value={formValues.icon}
                    onChange={handleFieldChange('icon')}
                    error={Boolean(fieldErrors.icon)}
                    helperText={fieldErrors.icon}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Image'
                    value={formValues.image}
                    onChange={handleFieldChange('image')}
                    error={Boolean(fieldErrors.image)}
                    helperText={fieldErrors.image}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Sort Order'
                    type='number'
                    value={formValues.sort_order}
                    onChange={handleFieldChange('sort_order')}
                    error={Boolean(fieldErrors.sort_order)}
                    helperText={fieldErrors.sort_order}
                  />
                </Grid>
              </Grid>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                className='admin-crud-actions'
              >
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={editingId ? <SaveRoundedIcon /> : <AddRoundedIcon />}
                  className='admin-crud-primaryButton'
                  disabled={isSaving}
                >
                  {isSaving
                    ? editingId
                      ? 'Updating...'
                      : 'Creating...'
                    : editingId
                      ? 'Update category'
                      : 'Add category'}
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={resetForm}
                  className='admin-crud-secondaryButton'
                >
                  Reset
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, xl: 7 }}>
          <Paper className='admin-dashboard-sideCard admin-crud-tableCard' elevation={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  Live records
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {section.label} list
                </Typography>
              </Box>
              <Chip
                label={isLoading ? 'Loading...' : `${items.length} rows`}
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-note'>
              Categories are loaded from `/admin/categories/list` and managed
              through the protected create, update, and delete endpoints.
            </Typography>
            <Divider className='admin-dashboard-divider' />

            <TableContainer className='admin-crud-tableWrap'>
              <Table className='admin-crud-table'>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell key={column.key}>{column.label}</TableCell>
                    ))}
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 2}>
                        <Typography className='admin-dashboard-note'>
                          Loading categories...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!isLoading &&
                    items.map((item) => (
                      <TableRow key={item.category_id} hover>
                        <TableCell>{item.category_id}</TableCell>
                        {visibleColumns.map((column) => (
                          <TableCell key={column.key}>
                            {item[column.key] || '-'}
                          </TableCell>
                        ))}
                        <TableCell align='right'>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent='flex-end'
                            className='admin-crud-rowActions'
                          >
                            <Button
                              size='small'
                              variant='text'
                              startIcon={<EditRoundedIcon />}
                              onClick={() => handleEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size='small'
                              color='error'
                              variant='text'
                              startIcon={<DeleteOutlineRoundedIcon />}
                              onClick={() => handleDelete(item)}
                              disabled={deletingId === item.category_id}
                            >
                              {deletingId === item.category_id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                  {!isLoading && items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 2}>
                        <Typography className='admin-dashboard-note'>
                          No categories returned by the API yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {section.stats?.length ? (
        <Box className='admin-dashboard-pillGrid'>
          {section.stats.map((item) => (
            <Box key={item} className='admin-dashboard-pill'>
              {item}
            </Box>
          ))}
        </Box>
      ) : null}
    </>
  );
}
